import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface PrivacyRow {
  readonly requestId: string;
  readonly requestCode: string;
  readonly requestType: string;
  readonly subjectName: string;
  readonly status: string;
  readonly priority: string;
  readonly slaBreached: boolean;
  readonly dueAt: string | null;
}

const TYPES = ['', 'ACCESS', 'CORRECTION', 'DELETION', 'RETENTION', 'CONSENT', 'PORTABILITY', 'OBJECTION', 'RESTRICTION'];
const STATUSES = ['', 'RECEIVED', 'VERIFYING', 'IN_PROGRESS', 'ON_HOLD', 'COMPLETED', 'REJECTED', 'CANCELLED'];
const CREATE_TYPES = TYPES.filter(Boolean);

/**
 * M30 — Privacy Request Dashboard (§17 / §31). Tracks data-subject access
 * requests (DSARs) — access, correction, deletion, portability, consent — with
 * their lifecycle status, priority, statutory due date and SLA-breach flag so
 * the DPO can fulfil each request within the regulated response window. The DPO
 * can intake a new request and progress or complete each one — wired to the
 * guarded /v1/privacy write API.
 */
export function M30PrivacyRequestPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<PrivacyRow[]>([]);
  const [requestType, setRequestType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // create form
  const [newType, setNewType] = useState('ACCESS');
  const [subjectName, setSubjectName] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (requestType) params.set('requestType', requestType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: PrivacyRow[] }>(`/v1/privacy/requests${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, requestType, status, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m30.privacy.notAuthorised', { verb, perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!subjectName.trim()) { setActionError(tr('m30.privacy.subjectRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: PrivacyRow }>(`/v1/privacy/requests`, {
        method: 'POST',
        body: { requestType: newType, subjectName: subjectName.trim() },
        token,
      });
      setNotice(tr('m30.privacy.logged', { code: r.data.requestCode }));
      setSubjectName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m30.privacy.verbLog'), 'M30_PRIVACY_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const runRowAction = async (
    fn: () => Promise<unknown>,
    successMsg: string,
    verb: string,
    perm: string,
  ): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const process = (p: PrivacyRow, targetStatus: string, label: string): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/privacy/requests/${p.requestId}/process`, { method: 'POST', body: { status: targetStatus }, token }),
      tr('m30.privacy.processed', { label, code: p.requestCode }), tr('m30.privacy.verbProcess'), 'M30_PRIVACY_MANAGE',
    );

  return (
    <main aria-label="privacy-requests">
      <h1>{tr('m30.privacy.title')}</h1>

      <section aria-label="create-privacy">
        <h2>{tr('m30.privacy.logHeading')}</h2>
        <form aria-label="privacy-form" onSubmit={(e) => void create(e)}>
          <label>{tr('m30.privacy.fieldType')}
            <select aria-label="field-request-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>{tr('m30.privacy.fieldSubject')}<input aria-label="field-subject-name" value={subjectName} onChange={(e) => setSubjectName(e.target.value)} /></label>
          <button type="submit" aria-label="submit-privacy" disabled={busy}>{busy ? tr('m30.privacy.submitLogging') : tr('m30.privacy.submitLog')}</button>
        </form>
        {actionError && <p role="alert" aria-label="privacy-error">{actionError}</p>}
        {notice && <p aria-label="privacy-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m30.privacy.filterType')}
          <select aria-label="filter-request-type" value={requestType} onChange={(e) => setRequestType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m30.privacy.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m30.privacy.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m30.privacy.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m30.privacy.loading')}</p> : (
        <>
          <p aria-label="privacy-count">{tr('m30.privacy.count', { count: rows.length })}</p>
          <table aria-label="privacy-table">
            <thead><tr><th>{tr('m30.privacy.thCode')}</th><th>{tr('m30.privacy.thType')}</th><th>{tr('m30.privacy.thSubject')}</th><th>{tr('m30.privacy.thStatus')}</th><th>{tr('m30.privacy.thPriority')}</th><th>{tr('m30.privacy.thSla')}</th><th>{tr('m30.privacy.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.requestId}>
                  <td>{p.requestCode}</td><td>{p.requestType}</td><td>{p.subjectName}</td>
                  <td>{p.status}</td><td>{p.priority}</td>
                  <td>{p.slaBreached ? tr('m30.privacy.slaBreached') : tr('m30.privacy.slaOk')}</td>
                  <td>
                    <button type="button" aria-label={`progress-${p.requestCode}`} onClick={() => void process(p, 'IN_PROGRESS', tr('m30.privacy.labelProgressed'))}>{tr('m30.privacy.btnProgress')}</button>
                    <button type="button" aria-label={`complete-${p.requestCode}`} onClick={() => void process(p, 'COMPLETED', tr('m30.privacy.labelCompleted'))}>{tr('m30.privacy.btnComplete')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
